import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GeniusIcon from "../../assets/images/gt-icon.png";
import AiIcon from "../../assets/images/icons/aiicon.svg";
import SendIcon from "../../assets/images/sendicon.svg";
import SearchGrow from "../../assets/images/lottieViews/search-grow.json";
import "./style.scss";
import RangeBar from "../slider/RangeBar";
import Slider from "react-slick";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useTranslationCustom from "../../i18n/customHook/useTranslation";
import { useDispatch, useSelector } from "react-redux";
import { getLoginConfig } from "../../redux/slices/loginconfigSlice";
import {
    getChatBotData,
    resetChatsInStore,
    setChatTransactionId,
    setGPTChats, setIsNormalFlow,
    updateQuestionState,
} from "../../redux/slices/chatBotSlice";
import {
    clearGPTLocationFilters,
    getSearchFilters,
    setGPTDeparture,
    setGPTInfants,
    setGPTLeaveTime,
    setGPTNightCount,
    setGPTPriceRange,
    setGPTTo,
    setGPTTravelers,
    setInitialChat,
    setPriceRangeValue,
    setTravelers,
} from "../../redux/slices/searchFilterSlice";
import {
    getAppConfig,
    resetAppConfig,
    resetChatGPTOffers,
} from "../../redux/slices/appconfigSlice";
import getChatQuestions from "./ChatQuestions";
import { toast } from "react-toastify";
import { ModifyQuestionTranslationText } from "../../i18n/TanslationHelper";
import { PostMessageToChatGPT } from "../../services/ChatGPTService";
import moment from "moment";
import "moment/locale/nl"; // Import Dutch locale for moment.js
import PropTypes from "prop-types";
import TypingDots from "../../assets/images/lottieViews/typing-dots.json";
import MapJson from '../../assets/images/lottieViews/map.json';
import Lottie from "lottie-react";
import { useNavigate } from "react-router-dom";
import { faArrowRotateRight, faUsers, faMoon, faArrowRightLong, faArrowLeftLong } from "@fortawesome/free-solid-svg-icons";
import { isMobile, isTablet } from 'react-device-detect';
import useKeyboardStatus from "../../helpers/Keyboard";
import DatePicker from "react-datepicker";
import { enUS, nl } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import {
    extractSearchFiltersFromHistory,
    getCustomOffers,
    getMessageQuery, getMessageText, getOffers,
    handleNavigationToMoreOffers,
    initializeChatScreen,
    initialQuestionState,
    isOffersMessage,
    parsePassengersFromMetaData,
    clearChatScrollRetries,
} from "./chatHelpers";
import HotelItemMapCard from "./HotelItemMapCard";
import TypingEffect from "./TypingEffect";
import { formatFlightSummary, getCurrencyBadge, extractDestinationDisplay } from "../../helpers/common";
import { getRequestAPIState, setResetHomePageToDefault } from "../../redux/slices/requestSlice";

// Track which offer messages have already been auto-slid (persists across renders)
const animatedMessagesMap = new Set();
// Track which carousel offers have been auto-slid (persists across component remounts)
const autoSlidOffers = new Set();

const parseChatGPTPrice = (answerGPT = "") => {
    const match = answerGPT.match(/\[PSEUDOCODE\]([\s\S]*?)\[\/PSEUDOCODE\]/i);
    const params = new URLSearchParams(match?.[1] || "");

    const parsePrice = (value) => {
        if (value === null || value === "") return null;

        const price = Number(value);
        return Number.isFinite(price) ? price : null;
    };

    return {
        low: parsePrice(params.get("MinPrice")),
        high: parsePrice(params.get("MaxPrice")),
    };
};

const ChatWizard = ({ hidden = true }) => {
    const dispatch = useDispatch();
    const { T } = useTranslationCustom();
    const navigate = useNavigate();
    const chatContainerRef = useRef(null);
    const chatWizardRef = useRef(null);

    const stopTypingForMessage = useCallback((messageTime) => {
        if (!messageTime) return;
        animatedMessagesMap.add(messageTime);
    }, []);

    // Helper function to reliably scroll to bottom
    const scrollToBottom = useCallback(() => {
        if (!chatContainerRef.current) return;

        // Use multiple requestAnimationFrame calls to ensure all DOM updates complete,
        // especially when images, carousels, or async content are being rendered
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (chatContainerRef.current) {
                    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;

                    // Add a small delay as fallback for image loading and carousel initialization
                    setTimeout(() => {
                        if (chatContainerRef.current) {
                            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                        }
                    }, 100);
                }
            });
        });
    }, []);

    const textareaRef = useRef(null);
    const isPriceAdjustingRef = useRef(false);  // Track if user is adjusting price
    const pendingPriceMessageRef = useRef(null);  // Store deferred price query to send after intermediary message animates
    const [message, setMessage] = useState("");
    const [finalQueryMessage, setFinalQueryMessage] = useState(null);
    const { resetHomePage } = useSelector(getRequestAPIState);
    const { GPTChat, questionState, chatTransactionId, isNormalFlow } = useSelector(getChatBotData);
    const { gptFilters, defaultPrice } = useSelector(getSearchFilters);
    const loginConfig = useSelector(getLoginConfig);
    const appConfig = useSelector(getAppConfig);
    const offers = appConfig.chatGPTOffers
        .filter((o) => o?.perPersonPrice?.totalPrice != null)
        .sort((a, b) => a.perPersonPrice.totalPrice - b.perPersonPrice.totalPrice);

    const [searchHistory, setSearchHistory] = useState([]);
    const [isSent, setIsSent] = useState(true);
    const [chats, setChats] = useState([]);
    const chatsRef = useRef(chats);
    chatsRef.current = chats;

    // Drops the most recent chat message tied to a search GUID. Used when the
    // search behind that message returns no offers or comes back with warnings,
    // so the conversation is not left with an empty offer message.
    const removeChatMessageBySearchGUID = useCallback((searchGUID) => {
        if (!searchGUID) return;

        const currentChats = chatsRef.current || [];
        // Chats are stored newest-first, so the first match is the last one added.
        const matchIndex = currentChats.findIndex(
            (item) => item?.messageData?.searchGUID === searchGUID
        );

        if (matchIndex === -1) return;

        const updatedChats = currentChats.filter((_, index) => index !== matchIndex);
        chatsRef.current = updatedChats;
        setChats(updatedChats);
        dispatch(setGPTChats(updatedChats));
    }, [dispatch]);

    const isTextareaFocusedRef = useRef(false);
    const iosSafariViewportFrameRef = useRef(null);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [price, setPrice] = useState(defaultPrice);
    const [tempPrice, setTempPrice] = useState(defaultPrice);
    const [offersFetched, setOffersFetched] = useState(offers?.length);
    const [activeIndices, setActiveIndices] = useState({});
    const isKeyboardOpen = useKeyboardStatus();
    const isMobileIOS =
        (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator?.platform === "MacIntel" && navigator.maxTouchPoints > 1))
        && /Mobile|Safari/.test(navigator.userAgent);
    const isIOSSafari =
        isMobileIOS &&
        /Safari/i.test(navigator.userAgent) &&
        !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(navigator.userAgent);
    const [date, setDate] = useState(new Date());
    const [open, setOpen] = useState(false);
    const [disable, setDisable] = useState(false);
    const [justArrived, setJustArrived] = useState(true);
    // const [inputPlaceholderText, setInputPlaceholderText] = useState(T("Chat:Chat_Bot_Input_Placeholder"));

    useEffect(() => {
        if (!(isMobile || isTablet)) return;

        if (!isKeyboardOpen) {
            if (isIOSSafari && isTextareaFocusedRef.current) return;

            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
            return;
        }

        if (!isMobileIOS) {
            textareaRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
            setTimeout(() => {
                window.scrollBy({ top: 30, behavior: "smooth" });
            }, 200);
        }
    }, [isIOSSafari, isKeyboardOpen, isMobileIOS]);

    useEffect(() => {
        if (!isMobileIOS || isIOSSafari || !window.visualViewport) return;

        const handleViewportChange = () => {
            if (!textareaRef.current) return;

            const viewport = window.visualViewport;
            const keyboardHeight = window.innerHeight - viewport.height - viewport.offsetTop;

            if (keyboardHeight > 150) {
                // Keyboard is open
                const textareaRect = textareaRef.current.getBoundingClientRect();
                const textareaBottom = textareaRect.bottom;
                const visibleBottom = viewport.height;

                // Calculate how much we need to scroll to put textarea above keyboard
                // Add 10px padding above keyboard
                const overlap = textareaBottom - visibleBottom + 10;

                if (overlap > 0) {
                    window.scrollBy({
                        top: overlap,
                        behavior: "smooth"
                    });
                }
            }
        };

        window.visualViewport.addEventListener("resize", handleViewportChange);

        return () => {
            window.visualViewport?.removeEventListener("resize", handleViewportChange);
        };
    }, [isIOSSafari, isMobileIOS]);

    // Cancel any pending offer-carousel scroll retries when the chat goes away;
    // otherwise three timeouts per search keep firing against a dead ref.
    useEffect(() => {
        return clearChatScrollRetries;
    }, []);

    const resetIOSSafariKeyboardLayout = useCallback(() => {
        document.body.classList.remove('ios-safari-keyboard-open');
        document.documentElement.style.removeProperty('--ios-safari-chatbox-height');
    }, []);

    const adjustIOSSafariTextareaPosition = useCallback(() => {
        if (!isIOSSafari || !isTextareaFocusedRef.current || !window.visualViewport || !chatWizardRef.current) return;

        if (iosSafariViewportFrameRef.current) {
            cancelAnimationFrame(iosSafariViewportFrameRef.current);
        }

        iosSafariViewportFrameRef.current = requestAnimationFrame(() => {
            const viewport = window.visualViewport;
            const chatWizard = chatWizardRef.current;

            if (!viewport || !chatWizard || !isTextareaFocusedRef.current) return;

            const keyboardHeight = window.innerHeight - viewport.height - viewport.offsetTop;

            if (keyboardHeight <= 150) return;

            const chatWizardRect = chatWizard.getBoundingClientRect();
            const chatWizardTop = Math.max(chatWizardRect.top - viewport.offsetTop, 0);
            const chatboxHeight = Math.max(viewport.height - chatWizardTop - 8, 120);

            document.body.classList.add('ios-safari-keyboard-open');
            document.documentElement.style.setProperty('--ios-safari-chatbox-height', `${chatboxHeight}px`);

            if (textareaRef.current) {
                textareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }

            if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }
        });
    }, [isIOSSafari]);

    useEffect(() => {
        if (!isIOSSafari || !window.visualViewport) return;

        const handleViewportChange = () => {
            adjustIOSSafariTextareaPosition();
            setTimeout(adjustIOSSafariTextareaPosition, 80);
            setTimeout(adjustIOSSafariTextareaPosition, 220);
        };

        window.visualViewport.addEventListener("resize", handleViewportChange);
        window.visualViewport.addEventListener("scroll", handleViewportChange);

        return () => {
            window.visualViewport?.removeEventListener("resize", handleViewportChange);
            window.visualViewport?.removeEventListener("scroll", handleViewportChange);

            if (iosSafariViewportFrameRef.current) {
                cancelAnimationFrame(iosSafariViewportFrameRef.current);
            }

            resetIOSSafariKeyboardLayout();
        };
    }, [adjustIOSSafariTextareaPosition, isIOSSafari, resetIOSSafariKeyboardLayout]);

    // Scroll to bottom when chats length changes (new messages added/removed) or when offers are fetched (carousel loads)
    useEffect(() => {
        scrollToBottom();
        // Add extra scroll attempts for carousel rendering with delays
        const timeouts = [
            setTimeout(() => scrollToBottom(), 100),
            setTimeout(() => scrollToBottom(), 300),
            setTimeout(() => scrollToBottom(), 600),
        ];
        return () => timeouts.forEach(t => clearTimeout(t));
    }, [chats.length, offersFetched, scrollToBottom]);

    // Process search history only when loginConfig.lastSearch actually changes
    useEffect(() => {
        if (loginConfig?.lastSearch?.length) {
            const validSearches = loginConfig.lastSearch.filter(data => {
                return (
                    data &&
                    Array.isArray(data.FlightSegmentList) &&
                    data.FlightSegmentList.length >= 2 &&
                    data.FlightSegmentList[0]?.Departure?.CodeList &&
                    data.FlightSegmentList[0]?.Arrival?.CodeList &&
                    data.FlightSegmentList[1]?.Departure?.Date &&
                    Array.isArray(data.PassengerAgeRoomItemList) &&
                    data.PassengerAgeRoomItemList.length > 0
                );
            });
            setSearchHistory(validSearches.map(data => formatFlightSummary(data, loginConfig?.locale?.code)));
        }
    }, [loginConfig.lastSearch, loginConfig?.locale?.code]);

    useEffect(() => {
        if (!justArrived) {
            const lengthExceeded = message.trim().split(/\s+/).length > 4;
            if (lengthExceeded && gptFilters?.initialChat) {
                dispatch(setIsNormalFlow(false)); // Manual user input
                setDisable(true);
                dispatch(clearGPTLocationFilters()); // Reseting GPT location filters on app load
            } else if (!isNormalFlow && isSent) {
                // Only reset isNormalFlow when not actively sending a message.
                // Without the isSent guard, setMessage("") inside handleOnSend
                // triggers this effect and resets isNormalFlow to true mid-flight,
                // which causes shouldAnimate to flip and TypingEffect to take over
                // rendering — breaking the slow-growth scroll behaviour.
                dispatch(setIsNormalFlow(true));
                setDisable(false);
            }
            // setInputPlaceholderText(T("Chat:Chat_Bot_Input_Placeholder"));
        } else {
            setJustArrived(false);
        }
    }, [message, finalQueryMessage]);

    useEffect(() => {
        if (hidden) return; // Do not initialize if hidden
        // Only initialize if chats are empty (first load or after reset)
        // Don't reinitialize when GPTChat updates from ongoing conversation
        if (chats.length === 0) {
            initializeChatScreen(GPTChat, dispatch, setChats, offers, setActiveIndices, questionState, gptFilters, T, loginConfig);
        }
    }, [GPTChat, hidden]);

    useEffect(() => {
        const handler = () => {
            resettingChatPage();
        };

        // Listen for global reset triggered from routes or other parts of the app
        window.addEventListener('app:resetHomePage', handler);

        // Backwards compatible: if the redux flag is already set, reset now
        if (resetHomePage) {
            resettingChatPage();
            dispatch(setResetHomePageToDefault());
        }

        return () => {
            window.removeEventListener('app:resetHomePage', handler);
        };
    }, [resetHomePage]);

    const setPriceHandler = (newPrice) => {
        setPrice(newPrice);
    }

    const handleNoCaseWithMethodForFlight = (_text) => {
        setChats(prevChats => [{
            text: _text,
            time: new Date(),
            clientSender: false,
        }, ...prevChats]);
    };

    // This function is updating price in the chat wizard an update <RangeBar /> component and also updating the price.
    const handlePriceUpdateChatWizard = (newPrice) => {
        setTempPrice(newPrice);
        setPrice(newPrice);
    }

    const handleOnSend = async (__message, overrideFilters = null, tempPriceOverride = null, skipSentMessage = false) => {
        let inputMessage;
        let localInitialChat = overrideFilters ? false : gptFilters?.initialChat;
        let modifiedFinalMessage = ''
        let _finalQueryMessageToDisplay = '';
        if (typeof __message === "string" || __message === null) {
            inputMessage = __message ? __message?.trim() : message?.trim();
            if (inputMessage?.trim() === "") {
                return;
            }
        } else if (typeof __message === "object" && !Array.isArray(__message)) {
            inputMessage = __message;
        }

        // If overrideFilters provided, skip the normal question flow
        if (gptFilters?.initialChat && isNormalFlow && !overrideFilters) {
            const _questions = [...questionState];
            let currentQuestion = _questions[questionIndex];

            if (
                currentQuestion?.id === "whereWantToGo" &&
                inputMessage !== T("Chat:Chat_Bot_Surprise_Me_Api")
            ) {
                dispatch(setGPTTo(inputMessage));
            } else if (currentQuestion?.id === "whereWantToGo" &&
                inputMessage === T("Chat:Chat_Bot_Surprise_Me_Api")
            ) {
                // Handle surprise me case
                dispatch(setGPTTo('Anywhere'));
            }
            else if (currentQuestion?.id === "fromAirport") {
                dispatch(setGPTDeparture(inputMessage));
            } else if (currentQuestion?.id === "travelerCount") {
                if (Number(gptFilters?.infants) + Number(inputMessage) > 6) {
                    return toast.info(T("Toasts:Max_Traveler_Description"));
                }
                dispatch(setGPTTravelers(inputMessage));
            } else if (currentQuestion?.id === "babyTravelerCount") {
                if (Number(gptFilters?.travelers) + Number(inputMessage) > 6) {
                    return toast.info(T("Toasts:Max_Traveler_Description"));
                }
                dispatch(setGPTInfants(inputMessage));
            } else if (currentQuestion?.id === "whenLeave") {
                // Ensure the date is stored with the current locale format
                const momentLocale = loginConfig?.locale?.code ? loginConfig.locale.code.split('_')[0].toLowerCase() : 'en';

                // Try parsing with current locale first, then fallback to English
                let parsedDate = moment(inputMessage, ["DD MMMM YYYY", "DD MMMM", "MMMM DD YYYY", "MMMM DD", moment.ISO_8601], momentLocale);

                if (!parsedDate.isValid()) {
                    // Try parsing with English locale if current locale fails
                    parsedDate = moment(inputMessage, ["DD MMMM YYYY", "DD MMMM", "MMMM DD YYYY", "MMMM DD", moment.ISO_8601], 'en');
                }

                const dateToStore = parsedDate.isValid()
                    ? parsedDate.locale(momentLocale).format("DD MMMM YYYY")
                    : inputMessage; // Fallback to original if parsing fails

                dispatch(setGPTLeaveTime(dateToStore));
            } else if (currentQuestion?.id === "howManyNights") {
                dispatch(setGPTNightCount(inputMessage));
            } else if (currentQuestion?.id === "howMuchSpend") {
                dispatch(setGPTPriceRange(inputMessage));
            }

            if (currentQuestion?.prompt === true) {
                currentQuestion = {
                    ...currentQuestion,
                    prompt: false,
                };
            }

            _questions[questionIndex] = {
                ...currentQuestion,
                prompt: false,
                time: new Date(),
                answer: inputMessage,
            };

            if (currentQuestion?.id === "howMuchSpend") {
                delete _questions[questionIndex]?.prompt;
                _questions[questionIndex] = {
                    ...currentQuestion,
                    answer: "",
                    priceRange: false
                };
            }

            dispatch(updateQuestionState(_questions));

            let newChats = [...chats];
            newChats = [...newChats].map((f) => {
                if (f.priceRange) {
                    let modifiedObject = { ...f };
                    modifiedObject.prompt && delete modifiedObject.prompt;
                    return modifiedObject;
                }
                if (f.datePicker) {
                    return { ...f, datePicker: false };
                }
                return { ...f, prompt: false };
            });

            if (currentQuestion?.prompt === false) {
                let userInput = inputMessage;
                if (
                    currentQuestion?.id === "whereWantToGo" &&
                    inputMessage === T("Chat:Chat_Bot_Surprise_Me_Api")
                ) {
                    userInput = T("Chat:Chat_Bot_Surprise_Me_Consumer_Input");
                }
                const sentMessage = {
                    text: userInput,
                    time: new Date(),
                    clientSender: true,
                };
                newChats.unshift(sentMessage);
            }

            setChats(newChats);
            setMessage("");

            if (questionIndex < 6) {
                const newQuestion = {
                    ..._questions[questionIndex + 1],
                    time: new Date(),
                };

                if (newQuestion?.prompt || newQuestion?.priceRange || newQuestion?.datePicker) {
                    setDisable(false);
                }

                const currentIndex = _questions.findIndex(
                    (f) => f?.id === currentQuestion?.id
                );
                setQuestionIndex(currentIndex + 1);

                newChats.unshift(newQuestion);
                setChats(newChats);
            } else {
                dispatch(setInitialChat(false));
                localInitialChat = false;
                _finalQueryMessageToDisplay = getMessageQuery(T, gptFilters, _questions, true, tempPriceOverride ? tempPriceOverride : price, getCurrencyBadge(loginConfig?.currency?.code));
                setFinalQueryMessage(
                    ModifyQuestionTranslationText({
                        message: _finalQueryMessageToDisplay,
                        params: {
                            travelers: gptFilters?.travelers,
                            infants: gptFilters?.infants,
                            nightsCount: gptFilters?.nightCount,
                        },
                        language: loginConfig.locale.short,
                    })
                ); // Final Query Text
                const _message = getMessageQuery(T, gptFilters, _questions, false, tempPriceOverride ? tempPriceOverride : price, getCurrencyBadge(loginConfig?.currency?.code)); // API input query Text
                modifiedFinalMessage = ModifyQuestionTranslationText({
                    message: _message,
                    params: {
                        travelers: gptFilters?.travelers,
                        infants: gptFilters?.infants,
                        nightsCount: gptFilters?.nightCount,
                    },
                    language: loginConfig.locale.short,
                });
            }

            if (modifiedFinalMessage === '' && localInitialChat === true) {
                return;
            } else {
                setFinalQueryMessage(null);
            }
        }

        const isPriceChangeOrRecentSearchMessage = (typeof __message === "string" &&
            __message?.trim() !== "" &&
            (!gptFilters.initialChat || !isNormalFlow)) || overrideFilters;

        if (isPriceChangeOrRecentSearchMessage) {
            modifiedFinalMessage = __message.trim();
            _finalQueryMessageToDisplay = __message.trim();
        }

        try {
            const messageObject = {
                locale: loginConfig?.locale?.code,
                Currency: loginConfig?.currency?.code,
                questionGPT: message || modifiedFinalMessage,
                chatTransactionId: chatTransactionId,
                transaction_id: appConfig?.transaction_id,
            };

            let _chats = [...chatsRef.current];

            if (!skipSentMessage) {
                const sentMessage = {
                    text: _finalQueryMessageToDisplay || message,
                    time: new Date(),
                    clientSender: !_finalQueryMessageToDisplay,
                };
                _chats.unshift(sentMessage);
            }

            _chats = _chats.map(item => {
                if (item.prompt !== undefined) {
                    return { ...item, prompt: false }; // safe clone
                }
                return item;
            });

            setChats(_chats);
            setMessage("");

            setIsSent(false);
            // setInputPlaceholderText(T("Chat:Chat_Bot_Waiting_Placeholder_Text"));
            setDisable(true);

            // if (!isNormalFlow && gptFilters?.to && gptFilters?.departure) {
            //     messageObject.questionGPT = `Destination: ${gptFilters?.to} | Departure: ${gptFilters?.departure} | ${messageObject.questionGPT}`;
            // }

            // Update the sent message immediately without the slow growth effect
            const chatResponse = await PostMessageToChatGPT(messageObject);

            const pattern = /\[PSEUDOCODE\].*?\[\/PSEUDOCODE\]/g;
            const chatGPTPrice = parseChatGPTPrice(chatResponse?.answerGPT);

            const apiResponseMessage = {
                text: chatResponse?.answerGPT?.replace(pattern, "") || "",
                messageData: chatResponse,
                time: new Date(),
                clientSender: false,
            };

            if (!chatTransactionId) {
                dispatch(setChatTransactionId(chatResponse?.chatTransactionId));
            }

            // This response contains search guid and metadata so this is an offer message.
            if (
                apiResponseMessage?.messageData?.searchGUID &&
                apiResponseMessage?.messageData?.metaData
            ) {
                apiResponseMessage.offerMessage = true;
                const updatedIndices = { ...activeIndices };
                updatedIndices[apiResponseMessage?.messageData?.searchGUID] = 1;
                setActiveIndices(updatedIndices);
            }

            if (
                isOffersMessage(apiResponseMessage) &&
                apiResponseMessage?.offerMessage === true
            ) {
                const metaData = apiResponseMessage.messageData.metaData;
                // Shared with the hotel search in getOffers, so the search and the
                // follow-up flight requests always describe the same travellers.
                const travelersInfo = parsePassengersFromMetaData(metaData).rooms.map(
                    ({ adult, child, infants }) => ({ adult, child, infants })
                );

                // If no valid rooms were parsed, default to 2 adults, 0 children, 0 infants
                dispatch(setTravelers(travelersInfo.length ? travelersInfo : [{ adult: 2, child: 0, infants: 0 }]));

                getOffers(
                    apiResponseMessage,
                    false,
                    appConfig,
                    loginConfig,
                    tempPriceOverride ? tempPriceOverride : price,
                    setIsSent,
                    dispatch,
                    setActiveIndices,
                    T,
                    setOffersFetched,
                    activeIndices,
                    chatContainerRef,
                    stopTypingForMessage,
                    setPriceHandler,
                    // setInputPlaceholderText,
                    handleNoCaseWithMethodForFlight,
                    chatGPTPrice,
                    handlePriceUpdateChatWizard,
                    removeChatMessageBySearchGUID,
                );
            } else {
                setIsSent(true);
                // setInputPlaceholderText(T("Chat:Chat_Bot_Input_Placeholder"));
                setDisable(true);
            }

            _chats.unshift(apiResponseMessage);

            // Keep the ref in sync right away so an in-flight search that needs to
            // remove this message sees it, even before the re-render lands.
            chatsRef.current = _chats;
            setChats(_chats);

            // TypingEffect component will handle the character-by-character animation
            // Save the chat in Redux immediately with full text
            dispatch(setGPTChats(_chats));
        } catch (e) {
            console.error("ChatGPT error", e);
            setIsSent(true);
            // setInputPlaceholderText(T("Chat:Chat_Bot_Input_Placeholder"));
        }
    };

    const getMinimumDate = () => moment().toDate(); // today
    const getMaximumDate = () => moment().add(332, "days").toDate(); // today + 332 days

    const showNumericKeyboard = () => {
        const _questions = [...questionState];
        const currentQuestion = _questions[questionIndex];
        return (
            gptFilters?.initialChat &&
            (currentQuestion?.id === "babyTravelerCount" ||
                currentQuestion?.id === "travelerCount" ||
                currentQuestion?.id === "howManyNights")
        );
    };

    const resettingChatPage = () => {
        setChats([]);
        setQuestionIndex(0);
        setMessage("");
        setIsSent(true);
        // setInputPlaceholderText(T("Chat:Chat_Bot_Input_Placeholder"));
        setDisable(false);
        setFinalQueryMessage(null);
        dispatch(resetChatsInStore());
        dispatch(resetChatGPTOffers());
        dispatch(resetAppConfig());
        dispatch(setIsNormalFlow(true));
        // Clear auto-slide tracking so new offers can slide again
        autoSlidOffers.clear();
    };

    const handleSearchHistoryClick = (itemId) => {
        if (isSent === false) return;
        const selectedSearch = loginConfig?.lastSearch?.find(search => search?.id === itemId);

        if (selectedSearch) {
            const extractedFilters = extractSearchFiltersFromHistory(selectedSearch);

            if (extractedFilters) {
                // Create the message
                const recentSearchMessage = T("Chat:Chat_Bot_Query_Input_For_Api", {
                    travelers: extractedFilters.travelers,
                    infants: extractedFilters.infants,
                    to: extractDestinationDisplay(extractedFilters.to),
                    leaveTime: extractedFilters.leaveTime,
                    nightsCount: extractedFilters.nightsCount,
                    from: extractedFilters.from,
                    low: `${getCurrencyBadge(loginConfig?.currency?.code)}${extractedFilters.low}`,
                    high: `${getCurrencyBadge(loginConfig?.currency?.code)}${extractedFilters.high}`,
                });

                // Update local state

                setTempPrice({
                    low: extractedFilters.low,
                    high: extractedFilters.high,
                });

                setPrice({
                    low: extractedFilters.low,
                    high: extractedFilters.high,
                });

                // Dispatch Redux actions
                dispatch(setIsNormalFlow(false));
                dispatch(setPriceRangeValue({
                    low: extractedFilters.low,
                    high: extractedFilters.high,
                }));

                // Pass filters directly to handleOnSend so it doesn't rely on Redux state
                handleOnSend(recentSearchMessage, extractedFilters, {
                    low: extractedFilters.low,
                    high: extractedFilters.high,
                });

                setTimeout(() => {
                    scrollToBottom();
                }, 300);
            }
        }
    };

    const singleChatItemImplRef = useRef(null);
    singleChatItemImplRef.current = ({ item, index }) => {
        const messageItem = item;
        const sliderRef = useRef(null);
        const nudgeTimers = useRef({ forward: null, back: null });
        const [currentSlide, setCurrentSlide] = useState(0);
        const hasSlid = useRef(false);  // Instance-level flag to prevent re-sliding on remounts
        const [isTypingComplete, setIsTypingComplete] = useState(false);
        // const [showTip, setShowTip] = useState(false);

        // Use searchGUID as the unique identifier for this carousel offer
        const searchGUID = useMemo(() => {
            return item?.messageData?.searchGUID || null;
        }, [item?.messageData?.searchGUID]);

        // Calculate offers for this specific message
        const offerSlides = useMemo(() => {
            if (!item?.messageData?.searchGUID || !item?.offerMessage) return [];
            return getCustomOffers(item, offers, price) || [];
        }, [item?.messageData?.searchGUID, item?.offerMessage, offers, price.low, price.high]);

        const shouldAnimate = useMemo(() => {
            const shouldAnim = !animatedMessagesMap.has(item.time) &&
                !item.clientSender &&
                index === 0 &&
                item.text && // Must have text to animate
                item.text.trim().length > 0; // Text must not be empty

            // If not animating, typing is already complete
            if (!shouldAnim && !isTypingComplete) {
                setIsTypingComplete(true);
            }

            return shouldAnim;
        }, [item.time, item.clientSender, index, item.text, isTypingComplete]);

        const isCrousalOffer = useMemo(() =>
            item?.offerMessage === true &&
            offersFetched &&
            offerSlides.length > 0
            // index === 0  // Only auto-slide the newest (first) offer
            , [item?.offerMessage, offersFetched, offers, index]);

        // Auto-slide effect - only run once when offer first appears, not on remounts
        useEffect(() => {
            // If this instance already slid, don't do it again (survives remounts via ref)
            if (hasSlid.current) {
                // setShowTip(true);
                return;
            }

            // Must be a carousel offer at index 0 (newest)
            if (!isCrousalOffer || index !== 0 || !searchGUID) {
                // setShowTip(true);
                return;
            }

            // Check module-level Set as backup - prevents sliding if already done in a previous session
            if (autoSlidOffers.has(searchGUID)) {
                hasSlid.current = true;  // Mark instance as done too
                // setShowTip(true);
                return;
            }

            // Not enough slides to animate
            if (offerSlides.length < 2) {
                // setShowTip(true);
                return;
            }

            // Mark this instance as having slid (prevents re-slide on remount)
            hasSlid.current = true;
            // DO NOT add to module-level Set yet - wait until animation completes

            // Retry logic to wait for slider to be ready
            let attempts = 0;
            const maxAttempts = 10;
            let retryTimer = null;

            const trySlide = () => {
                attempts++;

                if (!sliderRef.current || typeof sliderRef.current.slickGoTo !== "function") {
                    if (attempts < maxAttempts) {
                        retryTimer = setTimeout(trySlide, 300);
                    } else {
                        // Add to Set even if failed - don't keep retrying forever
                        autoSlidOffers.add(searchGUID);
                        // setShowTip(true);
                    }
                    return;
                }

                // Slide forward
                nudgeTimers.current.forward = setTimeout(() => {
                    if (sliderRef.current?.slickGoTo) {
                        sliderRef.current.slickGoTo(1);
                    }

                    // Slide back
                    nudgeTimers.current.back = setTimeout(() => {
                        if (sliderRef.current?.slickGoTo) {
                            sliderRef.current.slickGoTo(0);
                        }
                        // NOW add to Set - animation is complete
                        autoSlidOffers.add(searchGUID);
                        // setShowTip(true);
                    }, 900);
                }, 500);
            };

            // Start trying after a short delay for initial render
            retryTimer = setTimeout(trySlide, 600);

            return () => {
                clearTimeout(retryTimer);
                clearTimeout(nudgeTimers.current.forward);
                clearTimeout(nudgeTimers.current.back);
            };
        }, [isCrousalOffer, searchGUID, offerSlides.length, index]);

        function SampleNextArrow(props) {
            const canShow = currentSlide < offerSlides.length - 1;
            if (!canShow) return null;
            return (
                // <span
                //     className="fas fa-long-right slick-arrow arrow-right"
                //     onClick={props.onClick}
                // ></span>
                <FontAwesomeIcon icon={faArrowRightLong} className="slick-arrow arrow-right" onClick={props.onClick} />
            );
        }

        SampleNextArrow.propTypes = {
            onClick: PropTypes.func,
        }

        function SamplePrevArrow(props) {
            const canShow = currentSlide > 0;
            if (!canShow) return null;
            return (
                // <span
                //     className="fas fa-long-arrow-alt-left slick-arrow arrow-left"
                //     onClick={props.onClick}
                // ></span>
                <FontAwesomeIcon icon={faArrowLeftLong} className="slick-arrow arrow-left" onClick={props.onClick} />
            );
        }

        SamplePrevArrow.propTypes = {
            onClick: PropTypes.func,
        }

        const sliderSettings = useMemo(() => ({
            slidesToShow: 1,
            infinite: false,
            speed: 300,
            arrows: true,
            nextArrow: <SampleNextArrow />,
            prevArrow: <SamplePrevArrow />,
            centerPadding: "0%",
            beforeChange: (oldIndex, newIndex) => {
                setCurrentSlide(newIndex);
            },
            responsive: [
                {
                    breakpoint: 991,
                    settings: {
                        slidesToShow: 1,
                        centerPadding: "5%",
                    },
                },
                {
                    breakpoint: 767,
                    settings: {
                        slidesToShow: 1,
                    },
                },
                {
                    breakpoint: 576,
                    settings: {
                        slidesToShow: 1,
                        centerPadding: "0",
                    },
                },
            ],
        }), [offerSlides.length, currentSlide]);

        const DatePickerInput = () => {
            if (!isNormalFlow) return;
            return (
                <button className="yes" disabled={disable} onClick={() => setOpen(!open)}>
                    {T("Buttons:Button_Choose_Date")}
                </button>
            );
        };

        const messageText = useMemo(() => getMessageText(item), [item]);

        const handleTypingDone = useCallback(() => {
            setIsTypingComplete(true);
            animatedMessagesMap.add(item.time);
            scrollToBottom();

            // Step 2: After priceChangeMessage finishes animating, show intermediary message and fire handleOnSend simultaneously
            if (item.pendingIntermediaryAction && pendingPriceMessageRef.current) {
                const { message: pendingMsg, tempPrice: pendingTempPrice } = pendingPriceMessageRef.current;
                pendingPriceMessageRef.current = null;

                const intermediaryMessage = {
                    text: T("Chat:Chat_Bot_Ask_Me_About_Preferred_Destination"),
                    time: new Date(),
                    clientSender: false,
                };
                setChats(prev => [intermediaryMessage, ...prev]);

                // Fire the API call after a tick so the intermediary message state update commits first
                setTimeout(() => {
                    handleOnSend(pendingMsg, null, pendingTempPrice, true);
                }, 0);
            }
        }, [item.time, item.pendingIntermediaryAction]);

        // Scroll to bottom when typing is complete and carousel should render
        useEffect(() => {
            if (isTypingComplete && isCrousalOffer && isPriceAdjustingRef.current === false) {
                // Multiple scroll attempts with increasing delays to handle image loading
                const timeouts = [
                    setTimeout(() => scrollToBottom(), 50),
                    setTimeout(() => scrollToBottom(), 150),
                    setTimeout(() => scrollToBottom(), 300),
                    setTimeout(() => scrollToBottom(), 500),
                    setTimeout(() => scrollToBottom(), 800),
                    setTimeout(() => scrollToBottom(), 1200),
                ];
                return () => timeouts.forEach(t => clearTimeout(t));
            }
        }, [isTypingComplete, isCrousalOffer]);

        return (
            <div className="">
                <div className={item?.clientSender ? "user-chatbox" : "chatbox-ai"}>
                    <div className="d-flex gap-3">
                        {!item?.clientSender && (
                            <img
                                className="genius-icon"
                                src={GeniusIcon}
                                alt="genius-img"
                            />
                        )}

                        <div
                            className={`${item?.clientSender ? "" : "rangebar-box"}`}>
                            <div
                                style={item?.systemQuestion && !isNormalFlow && !item?.priceRange ? { backgroundColor: 'darkgray' } : {}}
                                className={`${item?.clientSender ? "user-chat-text" : "chat-text"}`}>
                                {shouldAnimate ? <TypingEffect
                                    text={messageText}
                                    onDone={handleTypingDone}
                                    onTyping={scrollToBottom}
                                    id={item?.id}
                                    greetingDoneHandler={() => {
                                        if (chats.length === 1 && chats[0].id === 'greetAndHowTo') {
                                            let _chats = [...chats];
                                            const secondMessage = {
                                                ...initialQuestionState(T, gptFilters)[0],
                                                time: new Date(),
                                                isSurpriseQuestion: true,
                                                prompt: true,
                                                systemQuestion: true,
                                            };
                                            _chats.unshift(secondMessage);
                                            setChats(_chats);
                                        }
                                    }}
                                    message={message}
                                /> : messageText}

                                {item?.priceRange && (
                                    <div>
                                        <div className="chat-text rangebar-chat">
                                            <RangeBar
                                                lowPrice={tempPrice.low}
                                                highPrice={tempPrice.high}
                                                onChangePriceHandler={async (_price) => {
                                                    isPriceAdjustingRef.current = true;
                                                    setTempPrice(_price);
                                                    // Reset flag after React finishes rendering (800ms buffer)
                                                    await new Promise(resolve => setTimeout(resolve, 800));
                                                    isPriceAdjustingRef.current = false;
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {item?.priceRange && isSent && (
                                <div className="chat-confirmation">
                                    <button
                                        className="yes"
                                        disabled={!isSent}
                                        // style={!isNormalFlow ? { backgroundColor: 'darkgray' } : {}}
                                        onClick={async () => {
                                            if (!isNormalFlow || !gptFilters.initialChat) {
                                                const priceChangeMessage = T("Chat:Chat_Bot_Query_Input_For_Api", {
                                                    travelers: gptFilters?.travelers,
                                                    infants: gptFilters?.infants,
                                                    to: gptFilters?.to,
                                                    leaveTime: gptFilters?.leaveTime,
                                                    nightsCount: gptFilters?.nightCount,
                                                    from: gptFilters?.departure,
                                                    low: `${getCurrencyBadge(loginConfig?.currency?.code)}${tempPrice?.low}`,
                                                    high: `${getCurrencyBadge(loginConfig?.currency?.code)}${tempPrice?.high}`,
                                                });

                                                // Set price state before awaiting handleOnSend so the
                                                // values are committed before the typing effect starts.
                                                // handleOnSend receives tempPrice via its third arg.
                                                dispatch(setPriceRangeValue(tempPrice));
                                                setPrice(tempPrice);

                                                await handleOnSend(priceChangeMessage, null, tempPrice);

                                                return;
                                            }

                                            let newChats = [...chats];
                                            const _questions = [...questionState];
                                            newChats = [...newChats].map((f) => {
                                                // Only remove priceRange from the current item being acted upon
                                                if (f.time === item.time && f.priceRange) {
                                                    return { ...f, priceRange: false };
                                                }
                                                return f;
                                            });

                                            if (item?.id === "howMuchSpend") {
                                                _questions[6] = {
                                                    text: getChatQuestions({ T, index: 13 }),
                                                    time: new Date(),
                                                    clientSender: false,
                                                    id: "howMuchSpend",
                                                    answer: "",
                                                    priceRange: true,
                                                    systemQuestion: true,
                                                };
                                                const newQuestion = {
                                                    ...initialQuestionState(T, gptFilters)[6],
                                                    priceRange: true,
                                                    time: new Date(),
                                                };
                                                newChats.unshift(newQuestion);
                                            }

                                            dispatch(updateQuestionState(_questions));
                                            setChats(newChats);

                                            let answer = {
                                                low: tempPrice?.low,
                                                high: tempPrice?.high,
                                            };

                                            setPrice(tempPrice);
                                            dispatch(setPriceRangeValue(answer));

                                            // Store the pending query for later execution
                                            pendingPriceMessageRef.current = { message: answer, tempPrice };

                                            setIsSent(false);
                                            // setInputPlaceholderText(T("Chat:Chat_Bot_Waiting_Placeholder_Text"));
                                            // Step 1: Show priceChangeMessage as AI bubble first
                                            const queryDisplayMsg = {
                                                text: T("Chat:Chat_Bot_Query_Input_For_Api", {
                                                    travelers: gptFilters?.travelers,
                                                    infants: gptFilters?.infants,
                                                    to: gptFilters?.to,
                                                    leaveTime: gptFilters?.leaveTime,
                                                    nightsCount: gptFilters?.nightCount,
                                                    from: gptFilters?.departure,
                                                    low: `${getCurrencyBadge(loginConfig?.currency?.code)}${tempPrice?.low}`,
                                                    high: `${getCurrencyBadge(loginConfig?.currency?.code)}${tempPrice?.high}`,
                                                }),
                                                time: new Date(),
                                                clientSender: false,
                                                pendingIntermediaryAction: true,
                                            };
                                            newChats.unshift(queryDisplayMsg);
                                            setChats(newChats);
                                        }}
                                    >
                                        {T("Buttons:Button_Apply")}
                                    </button>
                                </div>
                            )}

                            {item?.datePicker ? (
                                <div className="chat-confirmation date-button">
                                    {!open && (
                                        <button className="yes"
                                            disabled={disable}
                                            style={!isNormalFlow ? { backgroundColor: 'darkgray' } : {}}
                                            onClick={() => isNormalFlow && setOpen(true)}>
                                            {T("Buttons:Button_Choose_Date")}
                                        </button>
                                    )}
                                    {open && (
                                        <DatePicker
                                            open={open}
                                            selected={date}
                                            onChange={(d) => {
                                                setDate(d);
                                                setOpen(false);
                                                // Extract locale code (e.g., 'nl' from 'nl_NL') for moment.js
                                                const momentLocale = loginConfig?.locale?.code ? loginConfig.locale.code.split('_')[0].toLowerCase() : 'en';
                                                handleOnSend(moment(d).locale(momentLocale).format("DD MMMM YYYY"));
                                            }}
                                            minDate={getMinimumDate()}
                                            maxDate={getMaximumDate()}
                                            dateFormat="dd MMMM yyyy"
                                            showMonthYearDropdown
                                            fixedHeight
                                            peekNextMonth
                                            calendarClassName="date-picker-fixed-height"
                                            locale={loginConfig.locale.code === "nl_NL" ? nl : enUS}
                                            customInput={<DatePickerInput />}
                                        />
                                    )}
                                </div>
                            ) : null}

                            {item?.prompt && (
                                <div className="chat-confirmation">
                                    <button
                                        className="no"
                                        disabled={disable}
                                        style={!isNormalFlow ? { backgroundColor: 'darkgray' } : {}}
                                        onClick={async () => {
                                            if (!isNormalFlow) return;
                                            let newChats = [...chats];
                                            const _questions = [...questionState];

                                            newChats = [...newChats].map((f) => {
                                                return { ...f, prompt: false };
                                            });
                                            if (item?.id === "whereWantToGo") {
                                                if (gptFilters?.to && !gptFilters?.to?.includes('Anywhere') && !item?.promptShown) {
                                                    _questions[0] = {
                                                        text: getChatQuestions({ T, index: 11, to: gptFilters?.to }),
                                                        time: new Date(),
                                                        clientSender: false,
                                                        id: "whereWantToGo",
                                                        answer: "",
                                                        prompt: true,
                                                        promptShown: true,
                                                        prefilledTo: gptFilters?.to,
                                                        systemQuestion: true,
                                                    };
                                                    const newQuestion = {
                                                        ..._questions[0],
                                                        time: new Date(),
                                                    };
                                                    newChats.unshift(newQuestion);
                                                } else {
                                                    _questions[0] = {
                                                        text: getChatQuestions({ T, index: 0 }),
                                                        time: new Date(),
                                                        clientSender: false,
                                                        id: "whereWantToGo",
                                                        answer: "",
                                                        prompt: false,
                                                        systemQuestion: true,
                                                        surpriseMeText: false,
                                                    };
                                                    const newQuestion = {
                                                        ..._questions[0],
                                                        time: new Date(),
                                                    };
                                                    newChats.unshift(newQuestion);
                                                }
                                            } else if (item?.id === "fromAirport") {
                                                _questions[1] = {
                                                    text: getChatQuestions({ T, index: 1 }),
                                                    time: new Date(),
                                                    clientSender: false,
                                                    id: "fromAirport",
                                                    answer: "",
                                                    prompt: false,
                                                    systemQuestion: true
                                                };
                                                const newQuestion = {
                                                    ...initialQuestionState(T)[1],
                                                    time: new Date(),
                                                };
                                                newChats.unshift(newQuestion);
                                            } else if (item?.id === "travelerCount") {
                                                _questions[2] = {
                                                    text: getChatQuestions({ T, index: 2 }),
                                                    time: new Date(),
                                                    clientSender: false,
                                                    id: "travelerCount",
                                                    answer: "",
                                                    prompt: false,
                                                    systemQuestion: true,
                                                };
                                                const newQuestion = {
                                                    ..._questions[2],
                                                    time: new Date(),
                                                };
                                                newChats.unshift(newQuestion);
                                            } else if (item?.id === "babyTravelerCount") {
                                                _questions[3] = {
                                                    text: getChatQuestions({ T, index: 8 }),
                                                    time: new Date(),
                                                    clientSender: false,
                                                    id: "babyTravelerCount",
                                                    answer: "",
                                                    prompt: false,
                                                    systemQuestion: true
                                                };
                                                const newQuestion = {
                                                    ..._questions[3],
                                                    time: new Date(),
                                                };
                                                newChats.unshift(newQuestion);
                                            } else if (item?.id === "whenLeave") {
                                                _questions[4] = {
                                                    text: getChatQuestions({ T, index: 10 }),
                                                    time: new Date(),
                                                    clientSender: false,
                                                    id: "whenLeave",
                                                    answer: "",
                                                    prompt: false,
                                                    systemQuestion: true,
                                                };
                                                const newQuestion = {
                                                    ...initialQuestionState(T)[4],
                                                    time: new Date(),
                                                };
                                                newChats.unshift(newQuestion);
                                            } else if (item?.id === "howManyNights") {
                                                _questions[5] = {
                                                    text: getChatQuestions({ T, index: 12 }),
                                                    time: new Date(),
                                                    clientSender: false,
                                                    id: "howManyNights",
                                                    answer: "",
                                                    prompt: false,
                                                    systemQuestion: true,
                                                };
                                                const newQuestion = {
                                                    ...initialQuestionState(T)[5],
                                                    time: new Date(),
                                                };
                                                newChats.unshift(newQuestion);
                                            } else if (item?.id === "howMuchSpend") {
                                                _questions[6] = {
                                                    text: getChatQuestions({ T, index: 13 }),
                                                    time: new Date(),
                                                    clientSender: false,
                                                    id: "howMuchSpend",
                                                    answer: "",
                                                    priceRange: true,
                                                    systemQuestion: true,
                                                };
                                                const newQuestion = {
                                                    ...initialQuestionState(T)[6],
                                                    time: new Date(),
                                                    priceRange: true,
                                                };
                                                newChats.unshift(newQuestion);
                                            }

                                            dispatch(updateQuestionState(_questions));
                                            setChats(newChats);
                                        }}
                                    >
                                        {item?.surpriseMeText ? T("Buttons:Button_Choose_Destination") : T("Buttons:Button_No")}
                                    </button>

                                    <button
                                        className="yes"
                                        disabled={disable}
                                        style={!isNormalFlow ? { backgroundColor: 'darkgray' } : {}}
                                        onClick={async () => {
                                            if (!isNormalFlow) return;
                                            let answer = "";
                                            if (item?.id === "whereWantToGo") {
                                                const destination = item?.prefilledTo || gptFilters?.to;
                                                if (destination && !item.isSurpriseQuestion) {
                                                    answer = destination;
                                                } else {
                                                    answer = T("Chat:Chat_Bot_Surprise_Me_Api");
                                                }
                                            } else if (item?.id === "fromAirport") {
                                                // Falls back to the value snapshotted onto the question
                                                // when this confirmation was created, since gptFilters.departure
                                                // can be wiped by clearGPTLocationFilters() if the user types
                                                // a manual override elsewhere before clicking Yes.
                                                answer = item?.prefilledDeparture || gptFilters?.departure;
                                            } else if (item?.id === "travelerCount") {
                                                answer = gptFilters?.travelers;
                                            } else if (item?.id === "babyTravelerCount") {
                                                answer = gptFilters?.infants;
                                            } else if (item?.id === "whenLeave") {
                                                // Re-format leaveTime with the current locale
                                                answer = gptFilters?.leaveTime;
                                                if (answer) {
                                                    const momentLocale = loginConfig?.locale?.code ? loginConfig.locale.code.split('_')[0].toLowerCase() : 'en';

                                                    // Try parsing with current locale first, then fallback to English
                                                    let parsedDate = moment(answer, ["DD MMMM YYYY", "DD MMMM", "MMMM DD YYYY", "MMMM DD", moment.ISO_8601], momentLocale);

                                                    if (!parsedDate.isValid()) {
                                                        // Try parsing with English locale if current locale fails
                                                        parsedDate = moment(answer, ["DD MMMM YYYY", "DD MMMM", "MMMM DD YYYY", "MMMM DD", moment.ISO_8601], 'en');
                                                    }

                                                    answer = parsedDate.isValid()
                                                        ? parsedDate.locale(momentLocale).format("DD MMMM YYYY")
                                                        : gptFilters?.leaveTime; // Fallback to original if parsing fails
                                                }
                                            } else if (item?.id === "howManyNights") {
                                                answer = gptFilters?.nightCount;
                                            }

                                            handleOnSend(answer);
                                        }}
                                    >
                                        {item?.surpriseMeText ? T("Buttons:Button_Surprise_Me") : T("Buttons:Button_Yes")}
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="chat-time">
                            {moment(item?.time).format("HH:mm")}
                        </div>
                    </div>
                </div>

                {isCrousalOffer && isTypingComplete ? (
                    <>
                        <div className={`packages-chat-wrapper d-flex gap-3 mt-3 desktop-only ${offerSlides?.length && offerSlides?.length === 1 ? 'single-slide-item' : ''}`}>
                            {offerSlides.length > 0 &&
                                offerSlides.map((offer, offerIndex) => {
                                    return (
                                        <HotelItemMapCard
                                            item={offer}
                                            key={`${offer?.guidForBooking || searchGUID}-${offer?.id || 'offer'}-${offerIndex}`}
                                        />
                                    );
                                })}
                        </div>
                        <div className="mobile-only flight-slider">
                            <Slider ref={sliderRef} {...sliderSettings}>
                                {offerSlides.length > 0 &&
                                    offerSlides.map((offer, offerIndex) => {
                                        return (
                                            <HotelItemMapCard
                                                item={offer}
                                                key={`${offer?.guidForBooking || searchGUID}-${offer?.id || 'offer'}-${offerIndex}`}
                                            />
                                        );
                                    })}
                            </Slider>
                        </div>
                        <div className="d-flex justify-content-center gap-3 packages-action">
                            <div
                                className=""
                                onClick={async () => handleNavigationToMoreOffers('search', dispatch, item, messageItem, loginConfig, navigate, appConfig?.metaDataCountByGuid, appConfig?.priceExceedOffersByGuid)}
                            >
                                <a className="explore-link">
                                    <Lottie animationData={SearchGrow} loop={true} className='map_animation' />
                                    <span>{T("Chat:Explore_All_Offers")}</span>
                                </a>
                            </div>

                            <a
                                rel="noopener noreferrer"
                                className="show-map-link"
                                onClick={async () => handleNavigationToMoreOffers('map', dispatch, item, messageItem, loginConfig, navigate, appConfig?.metaDataCountByGuid, appConfig?.priceExceedOffersByGuid)}
                            >
                                <Lottie animationData={MapJson} loop={true} className='map_animation' />
                                <span>{T("Packages_Detail:Show_On_Map")}</span>
                            </a>
                        </div>

                        {/* {showTip && (
                            <div className="chatbox-ai mt-3">
                                <div className="d-flex gap-3">
                                    <img className="genius-icon" src={GeniusIcon} alt="genius-img" />
                                    <div className="rangebar-box">
                                        <div className="chat-text">
                                            {animatedMessagesMap.has(`tip-${item.time}`)
                                                ? T("Chat:Chat_Bot_Tip_Text_If_User_Wants_Another_Locations")
                                                : (
                                                    <TypingEffect
                                                        text={T("Chat:Chat_Bot_Tip_Text_If_User_Wants_Another_Locations")}
                                                        onTyping={scrollToBottom}
                                                        onDone={() => animatedMessagesMap.add(`tip-${item.time}`)}
                                                    />
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )} */}

                    </>
                ) : null}
            </div>
        );
    };

    // Stable component wrapper - keeps the same reference across re-renders
    // so React doesn't unmount/remount chat items when parent state changes
    const SingleChatItem = useMemo(() => {
        const Wrapper = (props) => singleChatItemImplRef.current(props);
        Wrapper.propTypes = {
            item: PropTypes.object,
            index: PropTypes.number,
        };
        return Wrapper;
    }, []);

    return (
        <div ref={chatWizardRef} className={"chat-wizard-section container"}>
            <div className={"chatbox-wrapper"}>
                {searchHistory?.length > 0 && (
                    <div className="search-history-container">
                        <div className="search-history-items">
                            {searchHistory?.map((item, index) => (
                                <div
                                    key={index}
                                    className="search-history-item"
                                    onClick={() => handleSearchHistoryClick(item?.id)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <span className="history-field">
                                        {item?.departure}
                                    </span>
                                    <span className="history-separator">→</span>
                                    <span className="history-field">
                                        {item?.destination}
                                    </span>
                                    <span className="history-separator">|</span>
                                    <span className="history-field gap-1">
                                        <FontAwesomeIcon icon={faUsers} className="me-1" />
                                        {item?.travelers}
                                    </span>
                                    <span className="history-separator">|</span>
                                    <span className="history-field">
                                        {item?.duration}
                                        {" ("}
                                        {item?.nights}
                                        <FontAwesomeIcon icon={faMoon} className="ms-1" />
                                        {")"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <div ref={chatContainerRef} className="chatbox-body">
                    <div className={"d-flex flex-column-reverse mt-2"}>
                        {!hidden && chats?.map((chat, index) => (
                            <SingleChatItem item={chat} key={`${chat.time}-${index}`} index={index} />
                        ))}
                    </div>
                </div>

                {!isSent && (
                    <div className="chat-loading-icon">
                        <Lottie
                            animationData={TypingDots}
                            loop={true}
                            className="photo_animation"
                        />
                    </div>
                )}

                <div className="chat-footer-wrapper">
                    <div className="chatbox-footer">
                        <img src={AiIcon} alt="Ai-icon" />
                        <textarea
                            ref={textareaRef}
                            onFocus={() => {
                                document.body.classList.add('keyboard-open');
                                isTextareaFocusedRef.current = true;

                                if (isIOSSafari) {
                                    document.body.classList.add('ios-safari-keyboard-open');
                                    setTimeout(adjustIOSSafariTextareaPosition, 80);
                                    setTimeout(adjustIOSSafariTextareaPosition, 220);
                                }
                            }}
                            onBlur={() => {
                                document.body.classList.remove('keyboard-open');
                                isTextareaFocusedRef.current = false;

                                if (isIOSSafari) {
                                    resetIOSSafariKeyboardLayout();
                                }
                            }}
                            inputMode={showNumericKeyboard() ? "numeric" : "text"}
                            readOnly={!isSent}
                            rows={1}
                            value={finalQueryMessage || message}
                            onChange={(e) => {
                                const textarea = textareaRef.current;
                                if (!textarea) return;

                                const text = e.target.value;
                                setMessage(text);

                                const lineHeight = parseInt(
                                    window.getComputedStyle(textarea).lineHeight,
                                    10
                                );
                                const maxHeight = lineHeight * 5;

                                textarea.style.height = "auto";
                                textarea.style.height =
                                    Math.min(textarea.scrollHeight, maxHeight) + "px";

                                adjustIOSSafariTextareaPosition();
                                if (isIOSSafari) {
                                    setTimeout(adjustIOSSafariTextareaPosition, 80);
                                    setTimeout(adjustIOSSafariTextareaPosition, 220);
                                }
                            }}
                            onKeyDown={async (e) => {
                                const isMobileOrTablet =
                                    /android|iphone|ipad|ipod/i.test(navigator.userAgent.toLowerCase()) ||
                                    isMobile ||
                                    isTablet;

                                if (
                                    !isMobileOrTablet &&
                                    e.key === "Enter" &&
                                    !e.shiftKey &&
                                    (finalQueryMessage || message)?.trim()
                                ) {
                                    e.preventDefault();
                                    await handleOnSend(null);
                                }
                            }}

                            // Chat:Chat_Bot_Price_Exceed_Placeholder
                            // Chat:Chat_Bot_Flight_Modal_Stopover_Placeholder

                            placeholder={
                                !isSent
                                    ? T("Chat:Chat_Bot_Waiting_Placeholder_Text")
                                    : T("Chat:Chat_Bot_Input_Placeholder")
                            }

                            // Chat:Chat_Bot_Price_Exceed_Placeholder
                            // Chat:Chat_Bot_Flight_Modal_Stopover_Placeholder
                            // placeholder={inputPlaceholderText}
                            className="form-control"
                            style={{
                                resize: "none",
                                overflowY: "auto",
                                ...(isMobileIOS ? { fontSize: "16px", marginBottom: "0" } : {}),
                            }}
                        />

                        <a href="#">
                            <img
                                src={SendIcon}
                                alt="Ai-icon"
                                onClick={() => handleOnSend(null)}
                            />
                        </a>
                    </div>
                    <button className="reset-chat" onClick={resettingChatPage}>
                        <FontAwesomeIcon icon={faArrowRotateRight} /> {T("Buttons:Reset")}
                    </button>
                </div>
            </div>
        </div>
    );
};

ChatWizard.propTypes = {
    hidden: PropTypes.bool,
}

export default ChatWizard;
